import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Eye, EyeOff, Laptop, Loader2, LockKeyhole, LogOut } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabaseClient';
import { useAuth } from '../../../context/AuthContext';
import { useAppToast } from '../../../components/AppToast';
import { ModuleCard, ModuleErrorState, ModuleLoadingState } from '../../shared/components/ModulePageHeader';

interface SecuritySnapshot {
  lastLoginAt: string | null;
  email: string;
  sessionCreatedAt: string | null;
}

export const AccountSecurityPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { showToast, ToastContainer } = useAppToast();
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);

  // Form de senha
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);

  const securityQuery = useQuery({
    queryKey: ['account-security', user?.id],
    enabled: Boolean(user?.id),
    queryFn: async (): Promise<SecuritySnapshot> => {
      const [{ data: sessionData }, { data: userData }] = await Promise.all([
        supabase.auth.getSession(),
        supabase.auth.getUser(),
      ]);

      const authUser = userData.user ?? sessionData.session?.user;

      return {
        lastLoginAt: authUser?.last_sign_in_at ?? null,
        email: authUser?.email ?? user?.email ?? '-',
        sessionCreatedAt: sessionData.session?.user?.created_at ?? null,
      };
    },
  });

  const handleLogoutAll = async () => {
    setBusy(true);
    setFeedback(null);
    try {
      await supabase.auth.signOut({ scope: 'global' as any });
      await logout();
      navigate('/', { replace: true });
    } catch (error) {
      setFeedback(error instanceof Error ? error.message : 'Nao foi possivel encerrar todas as sessoes.');
    } finally {
      setBusy(false);
    }
  };

  const handleChangePassword = async () => {
    if (!newPassword || newPassword.length < 8) {
      showToast('warning', 'A nova senha deve ter pelo menos 8 caracteres.');
      return;
    }
    if (newPassword !== confirmPassword) {
      showToast('warning', 'A confirmação não corresponde à nova senha.');
      return;
    }
    setPasswordSaving(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      showToast('success', 'Senha atualizada com sucesso.');
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Falha ao atualizar a senha.';
      console.error('[AccountSecurity] change password:', error);
      showToast('error', message);
    } finally {
      setPasswordSaving(false);
    }
  };

  return (
    <div className="enterprise-page h-full overflow-y-auto">

      {securityQuery.isPending && <ModuleLoadingState label="Carregando segurança da conta" />}
      {securityQuery.isError && (
        <ModuleErrorState message="Não foi possível obter os dados de segurança neste momento." />
      )}

      <ModuleCard title="Alterar Senha" action={<LockKeyhole size={14} className="text-slate-500" />}>
        <div className="space-y-3">
          <div>
            <label className="enterprise-field-label mb-1.5 block">Senha atual</label>
            <input
              type={showPasswords ? 'text' : 'password'}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="Digite sua senha atual"
              className="enterprise-input w-full text-sm"
              autoComplete="current-password"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="enterprise-field-label mb-1.5 block">Nova senha</label>
              <input
                type={showPasswords ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Mínimo 8 caracteres"
                className="enterprise-input w-full text-sm"
                autoComplete="new-password"
              />
            </div>
            <div>
              <label className="enterprise-field-label mb-1.5 block">Confirmar nova senha</label>
              <input
                type={showPasswords ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Repita a nova senha"
                className="enterprise-input w-full text-sm"
                autoComplete="new-password"
              />
            </div>
          </div>
          <div className="flex items-center justify-between pt-1">
            <button
              type="button"
              onClick={() => setShowPasswords((v) => !v)}
              className="inline-flex items-center gap-1.5 text-[11px] enterprise-text-secondary hover:enterprise-text-primary"
            >
              {showPasswords ? <EyeOff size={12} /> : <Eye size={12} />}
              {showPasswords ? 'Ocultar senhas' : 'Mostrar senhas'}
            </button>
            <button
              type="button"
              onClick={handleChangePassword}
              disabled={passwordSaving || !newPassword || !confirmPassword}
              className="enterprise-button-primary inline-flex items-center gap-1.5 px-3 text-xs font-semibold disabled:cursor-not-allowed disabled:opacity-60"
            >
              {passwordSaving ? (
                <>
                  <Loader2 size={12} className="animate-spin" />
                  Atualizando…
                </>
              ) : (
                'Atualizar Senha'
              )}
            </button>
          </div>
        </div>
      </ModuleCard>

      <ModuleCard title="Sessões Ativas">
        <div className="enterprise-card-surface flex items-start justify-between gap-3 p-3">
          <div className="flex items-start gap-2">
            <Laptop size={16} className="mt-0.5 enterprise-text-secondary" />
            <div>
              <div className="text-sm font-semibold enterprise-text-primary">Sessão Atual</div>
              <div className="text-xs enterprise-text-secondary">{securityQuery.data?.email || user?.email || '-'}</div>
              <div className="mt-1 text-xs enterprise-text-secondary">
                Último login:{' '}
                <span className="enterprise-tech-value">
                  {securityQuery.data?.lastLoginAt
                    ? new Date(securityQuery.data.lastLoginAt).toLocaleString('pt-BR')
                    : 'Não disponível'}
                </span>
              </div>
            </div>
          </div>
          <span className="risk-ok rounded-md border border-enterprise-border-strong px-2 py-1 text-[10px] font-semibold uppercase tracking-wider">
            Ativa
          </span>
        </div>
      </ModuleCard>

      <ModuleCard title="Ações de Segurança">
        <button
          type="button"
          disabled={busy}
          onClick={handleLogoutAll}
          className="inline-flex items-center gap-1.5 rounded-md border border-enterprise-border-strong px-3 text-xs font-semibold risk-critical hover:bg-enterprise-panel disabled:opacity-60"
        >
          <LogOut size={14} />
          {busy ? 'Encerrando sessões...' : 'Logout de todas as sessões'}
        </button>
        <p className="mt-2 text-xs enterprise-text-secondary">
          Esta ação encerra sessões ativas em outros dispositivos.
        </p>
        {feedback && <p className="mt-2 text-xs risk-critical">{feedback}</p>}
      </ModuleCard>
      <ToastContainer />
    </div>
  );
};
